import { RamUsageInfo, GCUsageInfo } from "@app/shared";
import { HardwareStatusChart, HardwareStatusChartProps, SeriesDetails } from "./HardwareStatusChart";
import { TooltipItem } from "chart.js";
import { deepMerge, formatByteSize } from "../../../helpers";
import { ChartProps } from "react-chartjs-2";

type RamUsageChartProps = Omit<HardwareStatusChartProps<RamUsageInfo & GCUsageInfo>, "series" | "domain" | "yUnit">;

const dataAccessors = (index: keyof (RamUsageInfo & GCUsageInfo)) => {
    return {
        date: (data?: RamUsageInfo & GCUsageInfo) => data?.statusTimestamp,
        xAccessor: (data?: RamUsageInfo & GCUsageInfo) => data?.statusTimestamp,
        yAccessor: (data?: RamUsageInfo & GCUsageInfo) => {
            return data?.[index] as number | undefined;
        },
    };
};

const charts: SeriesDetails<RamUsageInfo & GCUsageInfo>[] = [
    {
        dataKey: "Physical RAM",
        accessors: dataAccessors("usedRam"),
    },
    {
        dataKey: "Fragmented Bytes",
        accessors: dataAccessors("fragmentedBytes"),
    },
    {
        dataKey: "Heap Bytes",
        accessors: dataAccessors("heapSizeBytes"),
    },
    {
        dataKey: "Available Bytes",
        accessors: dataAccessors("totalAvailableMemoryBytes"),
    },
    {
        dataKey: "Committed Bytes",
        accessors: dataAccessors("totalCommittedBytes"),
    },
];

export const RamUsageChart: React.FC<RamUsageChartProps> = ({ data = [], ...props }) => {
    const maxRam = data.find((val) => val?.maxRam)?.maxRam;
    const domain = [0, maxRam] as [number, number];
    const toMB = data.map(x => {
        x.heapSizeBytes /= Math.pow(2, 20);
        x.fragmentedBytes /= Math.pow(2, 20);
        x.totalAvailableMemoryBytes = (x.highMemoryLoadThresholdBytes - x.memoryLoadBytes) / Math.pow(2, 20); //totalAvailableMemoryBytes is mem+page size. Instead we'll show how may bytes until .net starts aggressively collecting and performance dives / we crash
        x.totalCommittedBytes /= Math.pow(2, 20);
        return x;
    });

    var chartProps = {
        options: {
            plugins: {
                tooltip: {
                    callbacks: {
                        label: function (tooltipItem: TooltipItem<"line">) {
                            return `mom ${charts[tooltipItem.datasetIndex].dataKey} ${formatByteSize(Math.round(tooltipItem.parsed.y * Math.pow(2, 20)))}`;
                        },
                        title: function (tooltipItems: TooltipItem<"line">[]) { return 'wtf' }
                    },
                },
            },
        },
    } as Partial<ChartProps<"line">>;
    const mergeProps = deepMerge(chartProps, props.chartjsOverrides as any) as any;
    return <HardwareStatusChart domain={domain} data={toMB} series={charts} chartjsOverrides={mergeProps} yUnit="MB" {...props} />;
};
